import { useCallback, useEffect, useRef, useState } from 'react';
import { getLocalToday } from '@/lib/date-utils';
import {
  getOfflineSessionOwner,
  isOwnedBy,
  useOfflineSessionOwner,
} from '@/lib/offline/session-offline-data';

// ─── Offline Draft persistence (localStorage) ──────────────────────────────

const DRAFT_PREFIX = 'daily_report_draft_';
const QUEUE_PREFIX = 'daily_report_submit_queue';

export interface DraftData {
  date: string;
  weather: string;
  shift_start: string;
  shift_end: string;
  crewIds: number[];
  crewRoles: Record<number, string>;
  crewHours: Record<number, string>;
  productive_hours: string;
  notes: string;
  photoFiles: string[]; // base64 data urls
  photoCaptions: string[];
  delays: DraftDelay[];
  supervisor: number | null;
}

export interface DraftDelay {
  delay_type: string;
  start_time: string;
  end_time: string;
  billable: boolean;
  client_approval_required: boolean;
  description: string;
  rate_override: string;
  photoFiles: string[];
  photoCaptions: string[];
}

function getEmptyDraft(): DraftData {
  return {
    date: getLocalToday(),
    weather: '',
    shift_start: '07:00',
    shift_end: '17:00',
    crewIds: [],
    crewRoles: {},
    crewHours: {},
    productive_hours: '',
    notes: '',
    photoFiles: [],
    photoCaptions: [],
    delays: [],
    supervisor: null,
  };
}

function draftKey(userId: number, orgId: number | null, projectId: number, reportId?: number): string {
  const orgKey = orgId ?? 'none';
  return `${DRAFT_PREFIX}${userId}_${orgKey}_${projectId}_${reportId ?? 'new'}`;
}

function readDraft(key: string | null): DraftData {
  if (!key) return getEmptyDraft();
  try {
    const stored = localStorage.getItem(key);
    const empty = getEmptyDraft();
    if (stored) {
      const parsed = { ...empty, ...JSON.parse(stored) };
      if (Array.isArray(parsed.delays)) {
        parsed.delays = parsed.delays.map((d: Partial<DraftDelay>) => ({
          delay_type: d.delay_type ?? 'weather',
          start_time: d.start_time ?? '',
          end_time: d.end_time ?? '',
          billable: d.billable ?? false,
          description: d.description ?? '',
          client_approval_required: d.client_approval_required ?? false,
          rate_override: d.rate_override ?? '',
          photoFiles: d.photoFiles ?? [],
          photoCaptions: d.photoCaptions ?? [],
        }));
      }
      return parsed;
    }
  } catch {
    // Ignore invalid persisted drafts.
  }
  return getEmptyDraft();
}

function hasStoredDraft(key: string | null): boolean {
  if (!key) return false;
  try {
    return localStorage.getItem(key) !== null;
  } catch {
    return false;
  }
}

export function useOfflineDraft(projectId: number, reportId?: number) {
  const owner = useOfflineSessionOwner();
  const key = owner
    ? draftKey(owner.userId, owner.orgId, projectId, reportId)
    : null;

  const [draft, setDraftState] = useState<DraftData>(() => readDraft(key));
  const [hasDraft, setHasDraft] = useState(() => hasStoredDraft(key));
  const [previousKey, setPreviousKey] = useState(key);

  if (previousKey !== key) {
    setPreviousKey(key);
    setDraftState(readDraft(key));
    setHasDraft(hasStoredDraft(key));
  }

  const draftRef = useRef(draft);
  // eslint-disable-next-line react-hooks/refs
  draftRef.current = draft;

  const setDraft = useCallback(
    (updater: DraftData | ((prev: DraftData) => DraftData)) => {
      setDraftState((prev) => {
        const next = typeof updater === 'function' ? updater(prev) : updater;
        if (key) {
          try {
            localStorage.setItem(key, JSON.stringify(next));
            setHasDraft(true);
          } catch {
            // localStorage full — ignore
          }
        } else {
          setHasDraft(false);
        }
        return next;
      });
    },
    [key]
  );

  const updateField = useCallback(
    <K extends keyof DraftData>(field: K, value: DraftData[K]) => {
      setDraft((prev) => ({ ...prev, [field]: value }));
    },
    [setDraft]
  );

  const clearDraft = useCallback(() => {
    if (key) localStorage.removeItem(key);
    setDraftState(getEmptyDraft());
    setHasDraft(false);
  }, [key]);

  return { draft, setDraft, updateField, clearDraft, hasDraft };
}

// ─── Submit queue for offline retry ─────────────────────────────────────────

export interface QueuedSubmission {
  id: string;
  projectId: number;
  payload: Record<string, unknown>;
  crewPayloads: Array<{
    person?: number;
    tech?: number;
    role: string;
    hours_on_site: string;
  }>;
  delayPayloads: DraftDelay[];
  photoDataUrls: string[];
  photoCaptions: string[];
  queuedAt: string;
  ownerUserId?: number | null;
  ownerOrgId?: number | null;
}

function submitQueueKey(userId: number, orgId: number | null): string {
  return `${QUEUE_PREFIX}_${userId}_${orgId ?? 'none'}`;
}

export function getSubmitQueue(): QueuedSubmission[] {
  const owner = getOfflineSessionOwner();
  if (!owner) return [];
  try {
    const raw = localStorage.getItem(submitQueueKey(owner.userId, owner.orgId));
    const queue: QueuedSubmission[] = raw ? JSON.parse(raw) : [];
    return Array.isArray(queue) ? queue.filter((item) => isOwnedBy(item, owner)) : [];
  } catch {
    return [];
  }
}

export function enqueueSubmission(item: QueuedSubmission): void {
  const owner = getOfflineSessionOwner();
  const key = owner ? submitQueueKey(owner.userId, owner.orgId) : QUEUE_PREFIX;
  let queue = owner ? getSubmitQueue() : [];
  if (!owner) {
    try {
      const stored = JSON.parse(localStorage.getItem(key) ?? 'null');
      if (Array.isArray(stored)) queue = stored;
    } catch {
      // Ignore malformed legacy queued submissions.
    }
  }
  const queuedItem = { ...item };
  if (owner) {
    queuedItem.ownerUserId = owner.userId;
    queuedItem.ownerOrgId = owner.orgId;
  } else {
    delete queuedItem.ownerUserId;
    delete queuedItem.ownerOrgId;
  }
  queue.push(queuedItem);
  localStorage.setItem(key, JSON.stringify(queue));
}

export function dequeueSubmission(id: string): void {
  const owner = getOfflineSessionOwner();
  if (!owner) return;
  const queue = getSubmitQueue().filter((q) => q.id !== id);
  localStorage.setItem(submitQueueKey(owner.userId, owner.orgId), JSON.stringify(queue));
}

export function useOnlineSync(onFlush: (item: QueuedSubmission) => Promise<void>) {
  const flushing = useRef(false);

  const flush = useCallback(async () => {
    if (flushing.current) return;
    flushing.current = true;
    try {
      const queue = getSubmitQueue();
      for (const item of queue) {
        if (!isOwnedBy(item, getOfflineSessionOwner())) break;
        try {
          await onFlush(item);
          dequeueSubmission(item.id);
        } catch {
          break; // stop on first failure, will retry later
        }
      }
    } finally {
      flushing.current = false;
    }
  }, [onFlush]);

  useEffect(() => {
    // flush on mount
    flush();

    const handler = () => {
      if (navigator.onLine) flush();
    };
    window.addEventListener('online', handler);
    return () => window.removeEventListener('online', handler);
  }, [flush]);
}

export function countDailyReportOfflineItems(): number {
  const keys = Object.keys(localStorage);
  let count = 0;
  for (const key of keys) {
    if (key.startsWith(DRAFT_PREFIX)) {
      count += 1;
    } else if (key.startsWith(QUEUE_PREFIX)) {
      try {
        const value = JSON.parse(localStorage.getItem(key) ?? 'null');
        if (Array.isArray(value)) count += value.length;
      } catch {
        // Ignore malformed queued submissions.
      }
    }
  }
  return count;
}

export function clearDailyReportOfflineData(): void {
  const keys = Object.keys(localStorage);
  for (const key of keys) {
    if (key.startsWith(DRAFT_PREFIX) || key.startsWith(QUEUE_PREFIX)) {
      localStorage.removeItem(key);
    }
  }
}
